import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Layout from "../Layout";
import { Button, TextBox } from "../../../../components";
import QuotationTenderingContext from "../QuotationTenderingContext";
import { Field, Form, Formik } from "formik";
import { ReactComponent as TrashSvg } from "../../../../assets/tendering/trash.svg";
import { ReactComponent as CircleEqualSvg } from "../../../../assets/tendering/circle-equal.svg";
import {
  formatNumber,
  numberToOrdinal,
} from "../../../../helpers/common.helper";
import _ from "lodash";
import * as Tooltip from "react-tooltip";

const PaymentTerms = (props) => {
  const { setStep, details, setDetails, goForward, goBack } = useContext(
    QuotationTenderingContext
  );
  const [advancedPayment, setAdvancedPayment] = useState(
    details?.payment?.advancedPayment ?? {
      percentage: 10,
      remark: "",
    }
  );
  const [paymentTerms, setPaymentTerms] = useState(
    details?.payment?.paymentTerms ?? [
      [
        {
          item: undefined,
          remark: undefined,
          days: undefined,
          percentage: 90,
        },
      ],
    ]
  );
  const [wht] = useState(details?.payment?.wht);
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(false);
  const formikRef = useRef(null);
  const [_terms, setTerms] = useState(2);
  const [final, setFinal] = useState({
    items: [null, null],
  });
  const [_subjects] = useState(
    details?.subjects ?? [
      {
        name: "Floor Finished Work",
        items: [
          {
            name: "Wood floor",
            symbol: "A",
            areas: [10, 10],
            unit: "sq.m.",
            material: 100,
            labor: 100,
          },
        ],
      },
    ]
  );

  const _initialValues = useMemo(() => {
    let terms = new Array(paymentTerms.length + 1);

    terms[0] = [
      {
        item: "advancedPayments",
        remark: advancedPayment.remark,
        days: advancedPayment.days,
        percentage: advancedPayment.percentage,
      },
    ];

    let _no = 0;

    for (let i = 0; i < paymentTerms.length; i++) {
      let _tmp = new Array(paymentTerms[i].length);

      for (let j = 0; j < paymentTerms[i].length; j++) {
        _no += 1;

        const element = paymentTerms[i][j];

        let _term = {};

        if (element.item !== undefined) _term.item = element.item;
        if (element.remark !== undefined) _term.remark = element.remark;
        if (element.days !== undefined) _term.days = element.days;
        if (element.percentage !== undefined)
          _term.percentage = element.percentage;

        _tmp[j] = _term;

        // terms[_no] = _term;
      }

      terms[i + 1] = _tmp;
    }

    return { terms, wht };
  }, [advancedPayment, paymentTerms]);

  const expectedGrandTotal = useMemo(
    () =>
      details?.subjects.reduce((prev, curr) => {
        let _subjectTotal = curr.items.reduce((prev, curr) => {
          return prev + _.sum(curr.areas) * _.sum([curr.material, curr.labor]);
        }, 0);

        return prev + _subjectTotal;
      }, 0),
    [details?.subjects]
  );

  useEffect(() => {
    setStep(2);
  }, []);

  const handleValidate = (values, options) => {
    const { terms } = values;

    let errors = {};

    let totalPercentage = 0;

    let _terms = new Array(_initialValues.length);

    for (let i = 0; i < terms.length; i++) {
      const term = terms[i];
      const isFinalPayment = terms.length === i + 1;

      let itemError = new Array(term.length);

      for (let j = 0; j < term.length; j++) {
        const { item, remark, days: _days, percentage } = term[j];

        totalPercentage += percentage ?? 0;

        let termError = {};

        if (!item) {
          termError.item = "require";
        }

        // * Check duplicate item
        if (
          i > 0 &&
          item &&
          terms.find((t, tIdx) => {
            if (tIdx === 0) return false;

            let _match = t.find((_it, itemIdx) => {
              if (i === tIdx && j == itemIdx) {
                return false;
              }

              return item === _it.item;
            });

            return !_.isEmpty(_match);
          })
        ) {
          termError.item = "duplicate";
        }

        if (!isFinalPayment) {
          const days = Number(_days);
          if (!days || days <= 0) {
            termError.days = "require";
          }
        }

        if (!percentage || percentage < 0 || percentage > 100) {
          termError.percentage = "require";
        }

        if (_.isEmpty(termError)) continue;

        itemError[j] = termError;
      }

      _terms[i] = itemError;
    }

    // * Apply error to all percentage
    if (totalPercentage !== 100) {
      _terms = _terms.map((term) =>
        term.map((item) => {
          item.percentage = "over";

          return item;
        })
      );
    }

    // * Only apply error if an error exists (limitation due to values being array)
    if (_terms.find((t) => t.find((i) => i !== null) !== undefined))
      errors.terms = _terms;

    return errors;
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const { advanced, subjects: calculatedSubjects } = _getData(values);

    setSubmitting(false);

    setDetails((curr) => ({
      ...curr,
      payment: {
        advancedPayment: advanced,
        paymentTerms: calculatedSubjects,
        wht: values.wht ?? false,
      },
    }));

    goForward();
  };

  const _subjectPayments = useMemo(() => {
    const _payments = [];

    for (const subject of _subjects) {
      _payments.push({
        items: subject.items.map(() => ({
          terms: new Array(_terms).fill(0, 0, _terms),
          final: {
            value: 0,
            remaining: true,
          },
        })),
      });
    }

    return _payments;
  }, [_terms, _subjects]);

  const getItemTotal = (item) => {
    return _.sum(item.areas) * _.sum([item.material, item.labor]);
  };

  const deductAdvanced = (total, advancedPercentage) => {
    return total * ((100 - advancedPercentage) / 100);
  };

  const _toNumber = (val) => {
    return isNaN(val) ? 0 : Number(val);
  };

  const handleDeleteTerm = (termIndex) => {
    formikRef.current.setFieldValue(
      "terms",
      formikRef.current.values.terms - 1
    );
  };

  const _getData = (values) => {
    const { subjects, terms, subjectPayments, advancedPayment } =
      _.cloneDeep(values);

    const advancedPercentage = isNaN(advancedPayment) ? 0 : advancedPayment;

    const advancedTotal = (advancedPercentage / 100) * expectedGrandTotal;

    const _subjects = [];

    for (let subjectIndex = 0; subjectIndex < subjects.length; subjectIndex++) {
      const subject = subjects[subjectIndex];

      const subjectPreTotal = subject.items.reduce((prev, curr) => {
        return prev + getItemTotal(curr);
      }, 0);

      const subjectTotal = (subjectPreTotal * (100 - advancedPercentage)) / 100;

      const payments = Array.from({ length: terms + 1 }, (_, i) => ({
        itemTotal: 0,
        percentage: 0,
      }));

      const items = [];

      for (let itemIndex = 0; itemIndex < subject.items.length; itemIndex++) {
        const item = subject.items[itemIndex];

        const itemPreTotal = getItemTotal(item);

        const itemTotal = deductAdvanced(itemPreTotal, advancedPercentage);

        const isRemaining =
          subjectPayments[subjectIndex]?.items[itemIndex]?.final?.remaining;

        let finalPercentage = 0;

        if (!isRemaining) {
          finalPercentage =
            subjectPayments[subjectIndex].items[itemIndex].final.value;
        }

        if (isRemaining) {
          finalPercentage =
            100 - _.sum(subjectPayments[subjectIndex].items[itemIndex].terms);
        }

        // * Accumalate item total foreach payment term
        let accumalatedTotal = 0;
        let accumalatedPercentage = 0;
        const itemPayments = [];

        for (
          let termIndex = 0;
          termIndex <
          subjectPayments[subjectIndex].items[itemIndex].terms.length;
          termIndex++
        ) {
          const itemPercentage =
            subjectPayments[subjectIndex].items[itemIndex].terms[termIndex] ??
            0;

          const itemTermTotal = itemTotal * (itemPercentage / 100);

          payments[termIndex].itemTotal += itemTermTotal;
          accumalatedTotal += itemTermTotal;
          accumalatedPercentage += itemPercentage;

          itemPayments.push({
            percentage: itemPercentage,
            total: itemTermTotal,
          });
        }

        const finalPayment =
          subjectPayments[subjectIndex].items[itemIndex].final;

        if (finalPayment.remaining) {
          const remainingTotal = itemTotal - accumalatedTotal;
          payments.at(-1).itemTotal += remainingTotal;

          const remainingPercentage = 100 - accumalatedPercentage;

          accumalatedPercentage = 100;

          itemPayments.push({
            percentage: remainingPercentage,
            total: remainingTotal,
          });
        }

        if (!finalPayment.remaining) {
          const remainingPercentage = _toNumber(finalPayment.value ?? 0);
          const itemFinalTotal = itemTotal * (remainingPercentage / 100);
          payments.at(-1).itemTotal += itemFinalTotal;
          accumalatedPercentage += remainingPercentage;

          itemPayments.push({
            percentage: remainingPercentage,
            total: itemFinalTotal,
          });
        }

        items.push({
          preTotal: itemPreTotal,
          total: itemTotal,
          isRemaining: isRemaining,
          finalPercentage: finalPercentage,
          totalPercentage: _toNumber(accumalatedPercentage).toFixed(2),
          payments: itemPayments,
        });
      }

      // * Calculate each paymentTerm percentage
      for (let payentIndex = 0; payentIndex < payments.length; payentIndex++) {
        const { itemTotal } = payments[payentIndex];
        payments[payentIndex].percentage = (
          (itemTotal / subjectTotal) *
          100
        ).toFixed(2);
      }

      _subjects.push({
        preTotal: subjectPreTotal,
        total: subjectTotal,
        items,
        payments,
      });
    }

    return {
      advanced: {
        percentage: advancedPercentage,
        total: advancedTotal,
      },
      advancedTotal,
      subjects: _subjects,
    };
  };

  const _renderHeader = (terms) => {
    return (
      <thead className="tendering-quotation__construction__payment-terms__table__header">
        <tr>
          <th style={{ minWidth: 20, textAlign: "center" }} rowSpan={2}>
            No
          </th>
          <th style={{ minWidth: 256 }} rowSpan={2}>
            Subject
          </th>
          <th style={{ minWidth: 100 }} rowSpan={2}>
            Price
          </th>
          <th style={{ minWidth: 100 }} rowSpan={2}>
            Net Price
          </th>
          {Array.from({ length: terms }, (_, i) => (
            <th style={{ minWidth: 180 }} rowSpan={2}>
              <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                <span>{numberToOrdinal(i + 1)} Payment</span>
                {terms > 1 && (
                  <div
                    className="tendering-quotation__construction__payment-terms__table__header__terms__delete"
                    onClick={() => handleDeleteTerm(i)}
                  >
                    <TrashSvg height={16} />
                  </div>
                )}
              </div>
            </th>
          ))}
          <th style={{ minWidth: 180, borderRight: "none" }}>Final Payment</th>
          <th style={{ minWidth: 16, borderLeft: "none" }} />
          <th style={{ minWidth: 120 }} rowSpan={2}>
            Total Percentage (%)
          </th>
        </tr>
        <tr>
          <th>Percentage (%)</th>
          <th style={{ width: 16 }}>
            <div
              data-tooltip-id="tooltip"
              data-tooltip-content="Remaining"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircleEqualSvg />
            </div>
          </th>
        </tr>
      </thead>
    );
  };

  return (
    <Layout>
      <Layout.Header>
        <h3>Payment Terms</h3>
        <Button
          label={"Add Payment Term"}
          size={"small"}
          onClick={() => {
            formikRef.current.setFieldValue(
              "terms",
              formikRef.current.values.terms + 1
            );
          }}
        />
      </Layout.Header>
      <Formik
        innerRef={formikRef}
        initialValues={{
          terms: _terms,
          final,
          subjects: _subjects,
          subjectPayments: _subjectPayments,
          wht,
        }}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, submitForm, setFieldValue }) => {
          const { subjects, terms, subjectPayments, advancedPayment } = values;

          const {
            advanced: { total: advancedTotal },
            subjects: calculatedSubjects,
          } = _getData(values);

          return (
            <>
              <Layout.Content className="tendering-quotation__construction__payment-terms">
                <Form className="tendering-quotation__construction__payment-terms__table-wrapper">
                  <div className="tendering-quotation__construction__payment-terms__scroller">
                    <table className="tendering-quotation__construction__payment-terms__table">
                      {_renderHeader(terms)}
                      <tbody className="tendering-quotation__construction__payment-terms__table__body">
                        {subjects.map((subject, subjectIndex) => {
                          const {
                            preTotal: subjectPreTotal,
                            total: subjectTotal,
                            items: calculatedItems,
                            payments,
                          } = calculatedSubjects[subjectIndex];

                          const subjectPercentage = payments
                            .reduce(
                              (prev, curr) => prev + Number(curr.percentage),
                              0
                            )
                            .toFixed(2);

                          return (
                            <>
                              <tr
                                key={`subject-${subjectIndex}`}
                                className="tendering-quotation__construction__payment-terms__table__body__subject"
                              >
                                <td style={{ textAlign: "center" }}>1</td>
                                <td>{subject.name}</td>
                                <td style={{ textAlign: "right" }}>
                                  {formatNumber(subjectPreTotal)}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {formatNumber(subjectTotal)}
                                </td>
                                {Array.from(
                                  { length: terms },
                                  (_tmp, termIndex) => {
                                    // * Accumalate total percentage for subject in this term
                                    const { percentage, itemTotal } =
                                      payments[termIndex];

                                    return (
                                      <td>
                                        <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                                          <span>{percentage}%</span>
                                          <span>{formatNumber(itemTotal)}</span>
                                        </div>
                                      </td>
                                    );
                                  }
                                )}
                                <td style={{ borderRight: "none" }}>
                                  <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                                    <span>{payments.at(-1).percentage}%</span>
                                    <span>
                                      {formatNumber(payments.at(-1).itemTotal)}
                                    </span>
                                  </div>
                                </td>
                                <td style={{ borderLeft: "none" }} />
                                <td style={{ textAlign: "right" }}>
                                  {subjectPercentage}%
                                </td>
                              </tr>
                              {subject.items.map((item, itemIndex) => {
                                const {
                                  preTotal: itemPreTotal,
                                  total: itemTotal,
                                  isRemaining,
                                  finalPercentage,
                                  totalPercentage,
                                } = calculatedItems[itemIndex];

                                return (
                                  <tr
                                    key={`subject-${subjectIndex}-items-${itemIndex}`}
                                  >
                                    <td />
                                    <td>{item.name}</td>
                                    <td style={{ textAlign: "right" }}>
                                      {formatNumber(itemPreTotal)}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      {formatNumber(itemTotal)}
                                    </td>
                                    {Array.from(
                                      { length: terms },
                                      (_, termIndex) => {
                                        return (
                                          <td>
                                            <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                                              <Field
                                                name={`subjectPayments[${subjectIndex}].items[${itemIndex}].terms[${termIndex}]`}
                                                type="number"
                                                style={{ width: 48 }}
                                              />
                                              <span>
                                                {formatNumber(
                                                  (itemTotal *
                                                    subjectPayments[
                                                      subjectIndex
                                                    ].items[itemIndex].terms[
                                                      termIndex
                                                    ]) /
                                                    100
                                                )}
                                              </span>
                                            </div>
                                          </td>
                                        );
                                      }
                                    )}
                                    <td>
                                      <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                                        {isRemaining && (
                                          <span>
                                            {finalPercentage.toFixed(2)}%
                                          </span>
                                        )}
                                        {!isRemaining && (
                                          <Field
                                            name={`subjectPayments[${subjectIndex}].items[${itemIndex}].final.value`}
                                            type="number"
                                            style={{ width: 48 }}
                                          />
                                        )}
                                        <span>
                                          {formatNumber(
                                            (finalPercentage * itemTotal) / 100
                                          )}
                                        </span>
                                      </div>
                                    </td>
                                    <td
                                      style={{
                                        textAlign: "center",
                                        padding: 0,
                                      }}
                                    >
                                      <Field
                                        name={`subjectPayments[${subjectIndex}].items[${itemIndex}].final.remaining`}
                                        type="checkbox"
                                        style={{
                                          width: 33,
                                          cursor: "pointer",
                                        }}
                                      />
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                      {totalPercentage}%
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          );
                        })}
                        <tr className="tendering-quotation__construction__payment-terms__table__body__advanced-separator">
                          <td colSpan={7 + terms} />
                        </tr>
                        <tr className="tendering-quotation__construction__payment-terms__table__body__advanced">
                          <td
                            colSpan={2}
                            className="tendering-quotation__construction__payment-terms__table__body__advanced__subject"
                          >
                            <div className="tendering-quotation__construction__payment-terms__table__body__space-between">
                              Advanced Payment (%)
                              <Field
                                name="advancedPayment"
                                type="number"
                                style={{ width: 60 }}
                              />
                            </div>
                          </td>
                          <td
                            className="tendering-quotation__construction__payment-terms__table__body__advanced__total"
                            colSpan={terms + 3}
                          >
                            {formatNumber(advancedTotal)}
                          </td>
                          <td colSpan={2} />
                        </tr>
                        <tr className="tendering-quotation__construction__payment-terms__table__body__subtotal">
                          <td colSpan={4} style={{ padding: "6px 12px" }}>
                            Sub Total
                          </td>
                          {Array.from({ length: terms + 1 }, (_, termIndex) => {
                            const paymentTotal = calculatedSubjects.reduce(
                              (prev, curr) => {
                                const _payments = curr.payments;

                                return prev + _payments[termIndex].itemTotal;
                              },
                              0
                            );

                            // if (termIndex === terms) {
                            //   return (
                            //     <td colSpan={2}>{formatNumber(paymentTotal)}</td>
                            //   );
                            // }

                            return <td>{formatNumber(paymentTotal)}</td>;
                          })}
                          <td colSpan={2} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Form>
                <div className="tendering-quotation__construction__payment-terms__summary">
                  <div className="tendering-quotation__construction__payment-terms__summary__tax">
                    <label className="tendering-quotation__construction__payment-terms__summary__tax__checkbox">
                      <Field type="checkbox" name="wht" />
                      Withhold Tax (3%)
                    </label>
                  </div>
                  <table className="tendering-quotation__construction__payment-terms__summary__table">
                    <tr>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__title">
                        Net Total
                      </td>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__value">
                        {formatNumber(expectedGrandTotal)}
                      </td>
                    </tr>
                    <tr>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__title">
                        VAT (7%)
                      </td>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__value">
                        +{formatNumber(expectedGrandTotal * 0.07)}
                      </td>
                    </tr>
                    {values.wht && (
                      <tr>
                        <td className="tendering-quotation__construction__payment-terms__summary__table__title">
                          WHT (3%)
                        </td>
                        <td className="tendering-quotation__construction__payment-terms__summary__table__value">
                          -{formatNumber(expectedGrandTotal * 1.07 * 0.03)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__title">
                        Grand Total
                      </td>
                      <td className="tendering-quotation__construction__payment-terms__summary__table__value">
                        {formatNumber(
                          expectedGrandTotal * 1.07 * (values.wht ? 0.97 : 1)
                        )}
                      </td>
                    </tr>
                  </table>
                </div>
              </Layout.Content>
              <Layout.Footer>
                <Button label="Return" variant={"secondary"} onClick={goBack} />
                <Button
                  type="submit"
                  label="Continue"
                  onClick={() => {
                    setValidateAfterSubmit(true);
                    submitForm();
                  }}
                />
              </Layout.Footer>
            </>
          );
        }}
      </Formik>
      <Tooltip.Tooltip id="tooltip" />
    </Layout>
  );
};

PaymentTerms.propTypes = {};

export default PaymentTerms;
